import { useSyncExternalStore } from "react";

/**
 * A clock that ticks once a minute, shared by every consumer.
 *
 * Settled state is a function of elapsed time, so it needs a clock — but not a
 * precise one. Quantising to the minute is what makes it usable:
 *
 * - **It is stable within a render pass.** Every surface resolving settled
 *   state reads the same string by construction, so the sidebar and a banner
 *   cannot disagree about whether the same thread has settled.
 * - **It memoises.** A raw `Date.now()` changes on every read, so any `useMemo`
 *   keyed on it recomputes every render — and the sidebar partition is exactly
 *   the kind of work that must not.
 * - **One timer, not one per component.** A module-level interval feeding
 *   `useSyncExternalStore` means a hundred rows cost one timer.
 *
 * The tick fires on UTC minute boundaries rather than every 60s from mount, so
 * consumers mounted at different times still flip together.
 */

function currentMinute(): string {
  return new Date().toISOString().slice(0, 16);
}

let nowMinute = currentMinute();
let intervalId: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function tick(): void {
  const next = currentMinute();
  if (next === nowMinute) return;
  nowMinute = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (intervalId === null) {
    // Every second, not every minute: a 60s timer started mid-minute would lag
    // the boundary by up to 59s, and browsers throttle background timers
    // enough that a coarse one drifts further still.
    intervalId = setInterval(tick, 1000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}

function getSnapshot(): string {
  return nowMinute;
}

/** The current UTC minute as `YYYY-MM-DDTHH:MM`. */
export function useNowMinute(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
